import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOne, useUpdate, useCreate, useList, useApiUrl } from "@refinedev/core";
import axios from "axios";
import { VaultSidebar, VaultDocItem } from "@/components/digibox/VaultSidebar";
import { VaultArticle } from "@/components/digibox/VaultArticle";
import { VaultActionBar } from "@/components/digibox/VaultActionBar";
import { VaultCommentPanel } from "@/components/digibox/VaultCommentPanel";
import { VaultDocumentPanel } from "@/components/digibox/VaultDocumentPanel";
import { usePortalContact } from "@/hooks/usePortalContact";

/**
 * Map acceptance status to sidebar status display.
 */
function mapAcceptanceStatus(status?: string): VaultDocItem["status"] {
  if (!status || status === "Pending") return "action";
  if (status === "Final Acceptance" || status === "Accepted") return "accepted";
  if (status === "Read") return "read";
  return "pending";
}

export const PolicyShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const { contactId } = usePortalContact();
  const [actionLoading, setActionLoading] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [relatedDocs, setRelatedDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);

  // Fetch current policy
  const { data: policyData, isLoading: policyLoading } = useOne({
    resource: "policies",
    id: id!,
  });

  // Fetch all policies for sidebar
  const { data: allPoliciesData, isLoading: policiesLoading } = useList({
    resource: "policies",
    pagination: { pageSize: 50 },
    sorters: [{ field: "title", order: "asc" }],
  });

  // Fetch acceptance data — coreBOS portal session handles scoping
  const { data: acceptanceData, refetch: refetchAcceptance } = useList({
    resource: "acceptance",
    pagination: { pageSize: 100 },
  });

  const { mutateAsync: updateAcceptance } = useUpdate();
  const { mutateAsync: createAcceptance } = useCreate();

  const policy = policyData?.data as any;
  const allPolicies = (allPoliciesData?.data || []) as any[];
  const acceptances = (acceptanceData?.data || []) as any[];

  // Fetch related Documents for the current policy
  useEffect(() => {
    if (!id) return;

    const fetchRelatedDocuments = async () => {
      setDocsLoading(true);
      try {
        const response = await axios.post(`${apiUrl}/direct/get-related`, {
          id,
          module: "evPolicies",
          related_module: "Documents",
        });
        if (response.data.success && response.data.result) {
          const raw = response.data.result.records ?? response.data.result;
          const docs = Array.isArray(raw) ? raw : Object.values(raw);
          const sorted = (docs as any[]).sort((a: any, b: any) => {
            const dateA = new Date(a.createdtime || 0).getTime();
            const dateB = new Date(b.createdtime || 0).getTime();
            return dateB - dateA;
          });
          setRelatedDocs(sorted);
        }
      } catch {
        setRelatedDocs([]);
      } finally {
        setDocsLoading(false);
      }
    };

    fetchRelatedDocuments();
  }, [id, apiUrl]);

  // Match acceptance to policy via acctopolicy field
  const matchesPolicy = (a: any) => {
    if (a.acctopolicy && id) {
      return a.acctopolicy === id;
    }
    // Fallback: string matching in acctitle
    return a.acctitle?.includes(policy?.title || "");
  };

  const pendingAcceptance = acceptances.find(
    (a: any) =>
      matchesPolicy(a) &&
      (a.acceptance_status === "Pending" || !a.acceptance_status)
  );
  const completedAcceptances = acceptances.filter(
    (a: any) =>
      matchesPolicy(a) &&
      (a.acceptance_status === "Final Acceptance" ||
        a.acceptance_status === "Accepted" ||
        a.acceptance_status === "Read")
  );

  const isCompleted =
    justCompleted || (!pendingAcceptance && completedAcceptances.length > 0);

  // Build sidebar documents — coreBOS portal session handles scoping
  const documents: VaultDocItem[] = allPolicies.map((p: any) => {
    const relatedAcceptance = acceptances.find(
      (a: any) => a.acctopolicy === p.id
    );
    return {
      id: p.id,
      category: p.policy_type || "Policy",
      title: p.title || "Untitled",
      date: p.createdtime
        ? new Date(p.createdtime).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        : "—",
      status: relatedAcceptance
        ? mapAcceptanceStatus(relatedAcceptance.acceptance_status)
        : p.policystatus === "Active"
          ? "action"
          : "read",
    };
  });

  const handleSelect = (docId: string) => {
    if (docId !== id) {
      setJustCompleted(false);
      navigate(`/digibox/policies/${docId}`);
    }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      if (pendingAcceptance) {
        await updateAcceptance({
          resource: "acceptance",
          id: pendingAcceptance.id,
          values: {
            acceptance_status: "Final Acceptance",
            acclastdate: new Date().toISOString().slice(0, 10),
          },
        });
      } else {
        await createAcceptance({
          resource: "acceptance",
          values: {
            acctitle: `Accepted: ${policy?.title || id}`,
            acceptance_status: "Final Acceptance",
            acclastdate: new Date().toISOString().slice(0, 10),
            acctopolicy: id,
            accmethod: "Portal",
            assigned_user_id: "19x1",
            ...(contactId ? { pacclinktocontact: contactId } : {}),
            description: `Policy ${policy?.title || id} accepted via DigiBox`,
          },
        });
      }
      setJustCompleted(true);
      refetchAcceptance();
    } catch {
      // Error handled by refine notification provider
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkRead = async () => {
    setActionLoading(true);
    try {
      if (pendingAcceptance) {
        await updateAcceptance({
          resource: "acceptance",
          id: pendingAcceptance.id,
          values: {
            acceptance_status: "Read",
            acclastdate: new Date().toISOString().slice(0, 10),
          },
        });
      } else {
        await createAcceptance({
          resource: "acceptance",
          values: {
            acctitle: `Read: ${policy?.title || id}`,
            acceptance_status: "Read",
            acclastdate: new Date().toISOString().slice(0, 10),
            acctopolicy: id,
            accmethod: "Portal",
            assigned_user_id: "19x1",
            ...(contactId ? { pacclinktocontact: contactId } : {}),
            description: `Policy ${policy?.title || id} marked as read via DigiBox`,
          },
        });
      }
      setJustCompleted(true);
      refetchAcceptance();
    } catch {
      // Error handled by refine notification provider
    } finally {
      setActionLoading(false);
    }
  };

  // The last (most recent) related document provides the rendered content
  const lastDocument = relatedDocs.length > 0 ? relatedDocs[0] : null;
  const policyDescription = policy?.description || "";
  const documentContent = lastDocument?.notecontent || lastDocument?.description || "";

  const effectiveDate = policy?.modifiedtime
    ? new Date(policy.modifiedtime).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : undefined;

  return (
    <>
      <VaultSidebar
        documents={documents}
        activeId={id}
        onSelect={handleSelect}
        loading={policiesLoading}
      />

      <main className="vault-panel vault-main-content">
        {policyLoading ? (
          <div className="vault-loading">Loading...</div>
        ) : !policy ? (
          <div className="vault-empty">Document not found</div>
        ) : (
          <>
            <VaultArticle
              title={policy.title || "Untitled Document"}
              documentId={`POL-${id}`}
              effectiveDate={effectiveDate}
              policyDescription={policyDescription}
              documentContent={documentContent}
              documentLoading={docsLoading}
              mainHeading={
                policy.policy_type
                  ? `Obligations — ${policy.policy_type}`
                  : undefined
              }
            />

            <VaultDocumentPanel recordId={id!} module="evPolicies" allowUpload={policy?.allow_upload === "1" || policy?.allow_upload === true} />

            {pendingAcceptance && !isCompleted ? (
              <VaultActionBar
                statement="I acknowledge receipt of this policy update. I confirm that I have read, understood, and accept the terms and conditions outlined."
                buttonLabel="Accept & Confirm"
                requiresConfirmation
                confirmWord="accetto"
                onAction={handleAccept}
                loading={actionLoading}
                onToggleComments={() => setCommentsPanelOpen((v) => !v)}
                commentsOpen={commentsPanelOpen}
              />
            ) : isCompleted ? (
              <VaultActionBar
                statement=""
                buttonLabel=""
                onAction={async () => {}}
                completed
                completedLabel="Policy accepted"
                onToggleComments={() => setCommentsPanelOpen((v) => !v)}
                commentsOpen={commentsPanelOpen}
              />
            ) : (
              <VaultActionBar
                statement="Review this document and mark it as read when you have finished."
                buttonLabel="Mark as Read"
                onAction={handleMarkRead}
                loading={actionLoading}
                onToggleComments={() => setCommentsPanelOpen((v) => !v)}
                commentsOpen={commentsPanelOpen}
              />
            )}
          </>
        )}
      </main>

      {id && (
        <VaultCommentPanel
          relatedTo={id}
          open={commentsPanelOpen}
          onClose={() => setCommentsPanelOpen(false)}
        />
      )}
    </>
  );
};
